/*
 * 干线卸车信息（干线 + 自有路线）。读写均走卸车侧 tms_loading_* 的“已卸子集”：
 * - 展示：卸车时间 / 运单数量 / 应卸容器数量(todo) / 已卸包牌列表 / BOL照片(仅终点站)
 * - 保存：saveUnloadingDetailInfoForPortal（卸车时间 + BOL + 包牌已卸标记 addedBagNumbers/removedBagNumbers）
 * 容器信息已按需求移除；支线入库(InboundInfoSection)另走 saveTmsUnloadingDetailInfo。
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Button, message, Modal } from 'antd';
import { useMutation, useQuery } from '@apollo/client';
import dayjs, { Dayjs } from 'dayjs';
import { t } from 'i18n';
import { useStepDetailRender, type OptionalString, formatTimeDisplay } from '../hooks/useStepDetailRender';
import EditToolbar, { useEditToolbar } from '../../EditToolbar';
import { useWarehousePermissionGuardedStartEdit } from '../../../hooks/useWarehousePermissionGuardedStartEdit';
import { GET_TMS_TASK_DETAIL } from '../../../graphql/queries/getTmsTaskDetail';
import type { TmsTaskWaypointType, TmsTaskDetailType } from '../../../graphql/queries/getTmsTaskDetail';
import {
    SAVE_UNLOADING_DETAIL_INFO_FOR_PORTAL,
    type SaveUnloadingDetailInfoInput,
} from '../../../graphql/mutations/taskMutations';
import { TmsBusinessTypeEnum, WaypointTypeEnum, TmsOperationTypeEnum } from '../../../../../../enums/TmsEnum';
import BagInfoTable, { type BagInfoRow } from './BagInfoTable';
import {
    GET_UNLOADED_INFO_LIST,
    type GetUnloadedInfoListResponse,
    type GetUnloadedInfoListVariables,
} from '../../../graphql/queries/getUnloadedInfoList';
import {
    GET_UNLOADING_COUNT_SUMMARY,
    type GetUnloadingCountSummaryResponse,
    type GetUnloadingCountSummaryVariables,
} from '../../../graphql/queries/getUnloadingCountSummary';
import { translateBackendError } from '../utils/errorMessage';

interface UnloadingInfoSectionProps {
    waypoint: TmsTaskWaypointType;
    stepDetailAllowEdit: boolean;
    onOpenUnloadingDetailDrawer?: (waypointId: number) => void;
    task?: TmsTaskDetailType;
}

const UnloadingInfoSection: React.FC<UnloadingInfoSectionProps> = ({ waypoint, stepDetailAllowEdit, onOpenUnloadingDetailDrawer, task }) => {
    const [unLoadingTimeDisplay, setUnLoadingTimeDisplay] = useState<OptionalString>(
        formatTimeDisplay(waypoint.unLoadingTime)
    );
    const [unLoadingTimeTimezone] = useState<OptionalString>(waypoint.unLoadingTimeTimezone);
    const [bolPhotoUrls, setBolPhotoUrls] = useState<string[]>(
        waypoint.bolPhotosUrlsJson || []
    );

    const [unLoadingTimeDraft, setUnLoadingTimeDraft] = useState<Dayjs | null>(
        waypoint.unLoadingTime ? dayjs(waypoint.unLoadingTime) : null
    );
    const [bolPhotosDraft, setBolPhotosDraft] = useState<string[]>(waypoint.bolPhotosJson || []);
    const [remarkDraft, setRemarkDraft] = useState<string>(waypoint.bolRemark || '');
    const [bagRowsDraft, setBagRowsDraft] = useState<BagInfoRow[]>([]);
    const [isBagTableEditing, setIsBagTableEditing] = useState(false);
    const bagRowsSnapshotRef = React.useRef<BagInfoRow[] | null>(null);

    const taskWaypointId = Number(waypoint.id);

    // 卸车 tab 顶部计数：已卸运单数量 + 应卸容器数量，一个请求一并返回
    const { data: unloadingCountSummaryData } = useQuery<
        GetUnloadingCountSummaryResponse,
        GetUnloadingCountSummaryVariables
    >(GET_UNLOADING_COUNT_SUMMARY, {
        variables: { taskId: waypoint.taskId, taskWaypointId },
    });
    const parcelCount = unloadingCountSummaryData?.getUnloadingCountSummary.parcelCount ?? null;
    const expectedContainerCount = unloadingCountSummaryData?.getUnloadingCountSummary.expectedContainerCount ?? null;

    const { data: loadingInfoListData, loading: loadingInfoListLoading } = useQuery<
        GetUnloadedInfoListResponse,
        GetUnloadedInfoListVariables
    >(GET_UNLOADED_INFO_LIST, {
        variables: { taskId: waypoint.taskId, taskWaypointId },
    });

    const { isEditing, startEdit, handleCancel, exitEdit } = useEditToolbar({
        onStartEdit: useCallback(() => {
            setUnLoadingTimeDraft(waypoint.unLoadingTime ? dayjs(waypoint.unLoadingTime) : null);
            setBolPhotosDraft(waypoint.bolPhotosJson || []);
            setRemarkDraft(waypoint.bolRemark || '');
            // 进入编辑时保存快照，用于保存前计算本次新增/删除
            bagRowsSnapshotRef.current = bagRowsDraft.map((r) => ({ ...r }));
        }, [waypoint.unLoadingTime, waypoint.bolPhotosJson, waypoint.bolRemark, bagRowsDraft]),
    });
    const guardedStartEdit = useWarehousePermissionGuardedStartEdit(waypoint.waypointId, startEdit);

    const [saveUnloadingDetailMutation] = useMutation(SAVE_UNLOADING_DETAIL_INFO_FOR_PORTAL);

    const unloadingTimeLabel = t('tms.task.drawer.unloadingTime');
    const photoLabel = t('tms.task.drawer.unloadingBolPhotos');
    const unloadingInfoLabel = t('tms.task.drawer.unloadingInfo');
    const parcelCountLabel = t('tms.task.drawer.loadingInfoParcelCount');
    const expectedContainerCountLabel = t('tms.task.drawer.unloadingExpectedContainerCount');
    const bagInfoLabel = t('tms.task.drawer.loadingInfoBagInfo');
    const remarkLabel = t('common.remark');

    const { renderTimeRow, renderPhotoRow, renderRemarkRow } = useStepDetailRender({ isEditing });

    useEffect(() => {
        setBagRowsDraft([]);
    }, [taskWaypointId]);

    useEffect(() => {
        const list = loadingInfoListData?.getUnloadedInfoList ?? [];
        if (list.length === 0) return;
        const rows: BagInfoRow[] = list.map((item, idx) => ({
            key: `bag-${idx}-${item.barCode}`,
            bagNumber: item.barCode,
            destinationSortingCode: item.destinationSortingCode ?? '',
            parcelCount: item.parcelQuantity,
            isBag: item.isBag,
            validated: true,
        }));
        setBagRowsDraft(rows);
    }, [loadingInfoListData?.getUnloadedInfoList]);

    const resetBagRowsDraft = useCallback(() => {
        const list = loadingInfoListData?.getUnloadedInfoList ?? [];
        const rows: BagInfoRow[] = list.map((item, idx) => ({
            key: `bag-${idx}-${item.barCode}`,
            bagNumber: item.barCode,
            destinationSortingCode: item.destinationSortingCode ?? '',
            parcelCount: item.parcelQuantity,
            isBag: item.isBag,
            validated: true,
        }));
        setBagRowsDraft(rows);
    }, [loadingInfoListData?.getUnloadedInfoList]);

    const handleCancelAndReset = useCallback(() => {
        resetBagRowsDraft();
        bagRowsSnapshotRef.current = null;
        handleCancel();
    }, [handleCancel, resetBagRowsDraft]);

    useEffect(() => {
        setUnLoadingTimeDisplay(formatTimeDisplay(waypoint.unLoadingTime));
        setUnLoadingTimeDraft(waypoint.unLoadingTime ? dayjs(waypoint.unLoadingTime) : null);
    }, [waypoint.unLoadingTime]);

    useEffect(() => {
        setBolPhotoUrls(waypoint.bolPhotosUrlsJson || []);
    }, [waypoint.bolPhotosUrlsJson]);

    useEffect(() => {
        if (!isEditing) {
            setBolPhotosDraft(waypoint.bolPhotosJson || []);
            setRemarkDraft(waypoint.bolRemark || '');
        }
    }, [waypoint.bolPhotosJson, waypoint.bolRemark, isEditing]);

    const renderBagRowChanges = (rows: BagInfoRow[]) => {
        if (rows.length === 0) return null;
        return (
            <div className="mt-1 space-y-1 text-gray-700">
                {rows.map((row) => (
                    <div key={row.bagNumber} className="text-sm" style={{ textIndent: '1rem' }}>
                        {t('tms.task.drawer.loadingChangeConfirmDetail', {
                            bagNumber: row.bagNumber || '-',
                            type: row.isBag
                                ? t('tms.task.drawer.loadingChangeConfirmTypeBag')
                                : t('tms.task.drawer.loadingChangeConfirmTypeParcel'),
                            count: row.parcelCount ?? 0
                        })}
                    </div>
                ))}
            </div>
        );
    };

    const computeBagRowDiffForConfirm = () => {
        const snapshot = bagRowsSnapshotRef.current ?? [];
        const snapshotValidated = snapshot.filter((r) => r.validated);
        const currentValidated = bagRowsDraft.filter((r) => r.validated);

        const snapshotMap = new Map<string, BagInfoRow>();
        snapshotValidated.forEach((r) => snapshotMap.set(r.bagNumber, r));

        const currentMap = new Map<string, BagInfoRow>();
        currentValidated.forEach((r) => currentMap.set(r.bagNumber, r));

        const added: BagInfoRow[] = [];
        const removed: BagInfoRow[] = [];

        currentMap.forEach((row, bagNumber) => {
            if (!snapshotMap.has(bagNumber)) added.push(row);
        });
        snapshotMap.forEach((row, bagNumber) => {
            if (!currentMap.has(bagNumber)) removed.push(row);
        });

        return { added, removed };
    };

    const bagRowsSnapshotByBarCode = React.useMemo(() => {
        const snapshot = bagRowsSnapshotRef.current ?? [];
        const map: Record<string, BagInfoRow> = {};
        snapshot.forEach((r) => {
            const key = (r.bagNumber || '').trim();
            if (!key) return;
            map[key] = r;
        });
        return map;
    }, [isEditing]);

    const isBagEditDisabled = task?.businessType === TmsBusinessTypeEnum.TikTok
        || task?.businessType === TmsBusinessTypeEnum.TemuGA
        || task?.businessType === TmsBusinessTypeEnum.TemuSCF;

    const parcelCountDisplay = React.useMemo(() => {
        const bagSum = bagRowsDraft.filter((r) => r.validated).reduce((sum, r) => sum + (r.parcelCount || 0), 0);
        // 编辑态：表格即当前权威数据源，直接采信汇总（用户删空则显示 0）；
        // 非编辑态：表格未加载时（bagSum 为 0）回退后端统计值
        if (isEditing) return bagSum;
        return bagSum > 0 ? bagSum : parcelCount;
    }, [bagRowsDraft, parcelCount, isEditing]);

    const confirmBagRowChangesIfNeeded = async (): Promise<boolean> => {
        if (!isEditing) return true;
        const { added, removed } = computeBagRowDiffForConfirm();
        if (added.length === 0 && removed.length === 0) return true;

            return await new Promise<boolean>((resolve) => {
                Modal.confirm({
                    title: t('tms.task.drawer.unloadingChangeConfirmTitle'),
                    content: (
                        <div className="space-y-2 text-sm">
                            <div className="text-sm text-gray-700">{t('tms.task.drawer.unloadingChangeConfirmLead')}</div>
                            {added.length > 0 && (
                                <div>
                                    <div className="font-medium">{t('tms.task.drawer.loadingChangeConfirmAdded')}</div>
                                    {renderBagRowChanges(added)}
                                </div>
                            )}
                            {removed.length > 0 && (
                                <div>
                                    <div className="font-medium">{t('tms.task.drawer.loadingChangeConfirmRemoved')}</div>
                                    {renderBagRowChanges(removed)}
                                </div>
                            )}
                            <div className="pt-2 text-gray-500">{t('tms.task.drawer.unloadingChangeConfirmHint')}</div>
                        </div>
                    ),
                    okText: t('tms.task.drawer.loadingChangeConfirmOk'),
                    cancelText: t('tms.task.drawer.loadingChangeConfirmCancel'),
                    okButtonProps: { style: { backgroundColor: '#722ed1', borderColor: '#722ed1' } },
                    cancelButtonProps: { style: { color: '#722ed1', borderColor: '#722ed1' } },
                    width: 560,
                    onOk: () => resolve(true),
                    onCancel: () => resolve(false),
                });
            });
    };

    const validateSave = () => {
        if (isBagTableEditing) {
            message.error(t('tms.task.drawer.bagTableEditingNotSaved'));
            return false;
        }
        if (!unLoadingTimeDraft) {
            message.error(t('tms.task.drawer.unloadingTimeRequired'));
            return false;
        }
        // BOL 照片仅终点站要求：途经点卸车不强制上传
        if (waypoint.waypointType === WaypointTypeEnum.Destination && bolPhotosDraft.length === 0) {
            message.error(t('tms.task.drawer.bolPhotosRequired'));
            return false;
        }
        return true;
    };

    const handleSave = async () => {
        if (!validateSave()) return;
        const okToSave = await confirmBagRowChangesIfNeeded();
        if (!okToSave) return;

        const { added, removed } = computeBagRowDiffForConfirm();
        // 按行类型分组传给后端（isBag 来自扫描校验/已卸回显）：包牌走 BagNumbers、运单走 TrackingNumbers
        const bagNumbers = (rows: typeof added) => rows.filter((r) => r.isBag).map((r) => r.bagNumber);
        const trackingNumbers = (rows: typeof added) => rows.filter((r) => !r.isBag).map((r) => r.bagNumber);

        const unloadingLocalTime = unLoadingTimeDraft ? unLoadingTimeDraft.format('YYYY-MM-DD HH:mm') : null;
        const input: SaveUnloadingDetailInfoInput = {
            taskId: waypoint.taskId,
            taskWaypointId: taskWaypointId,
            bolRemark: remarkDraft || null,
            bolPhotosJson: bolPhotosDraft,
            unloadingLocalTime,
            addedBagNumbers: bagNumbers(added),
            addedTrackingNumbers: trackingNumbers(added),
            removedBagNumbers: bagNumbers(removed),
            removedTrackingNumbers: trackingNumbers(removed),
        };
        const refetchQueries = [
            { query: GET_TMS_TASK_DETAIL, variables: { taskId: String(waypoint.taskId) } },
            { query: GET_UNLOADED_INFO_LIST, variables: { taskId: waypoint.taskId, taskWaypointId } },
            { query: GET_UNLOADING_COUNT_SUMMARY, variables: { taskId: waypoint.taskId, taskWaypointId } },
        ];

        try {
            await saveUnloadingDetailMutation({
                variables: { input },
                refetchQueries,
                awaitRefetchQueries: true,
            });

            setUnLoadingTimeDisplay(unLoadingTimeDraft ? unLoadingTimeDraft.format('YYYY-MM-DD HH:mm') : null);
            message.success(t('tms.task.drawer.saveSuccess'));
            exitEdit();
            bagRowsSnapshotRef.current = null;
        } catch (error: any) {
            console.error('Failed to save unloading detail:', error);
            message.error(translateBackendError(error));
        }
    };

    return (
        <div className="relative border-gray-200">
            <EditToolbar
                isEditing={isEditing}
                allowEdit={stepDetailAllowEdit && !loadingInfoListLoading}
                onStartEdit={guardedStartEdit}
                onSave={handleSave}
                onCancel={handleCancelAndReset}
            />
            <div className="mt-1 space-y-3">
                {/* 装车时间 */}
                {renderTimeRow(
                    unloadingTimeLabel,
                    unLoadingTimeDisplay,
                    unLoadingTimeTimezone,
                    unLoadingTimeDraft,
                    setUnLoadingTimeDraft,
                    true,
                    waypoint.unLoadingTimeTimezone
                )}
                {/* 装车信息 */}
                <div className="border-b border-gray-100">
                    <div className="text-sm text-gray-500 pb-3">{unloadingInfoLabel}:</div>
                    <div className="mt-2 pl-4 space-y-2">
                        {/* 运单数量：优先按表格明细汇总；否则展示后端统计 */}
                        <div className="flex items-center gap-3 min-h-[28px] pb-3">
                            {onOpenUnloadingDetailDrawer && !isEditing ? (
                                <Button
                                    type="link"
                                    className="p-0 h-auto text-left"
                                    onClick={() => onOpenUnloadingDetailDrawer(taskWaypointId)}
                                >
                                    <span className="text-sm text-gray-500 shrink-0">{parcelCountLabel}:</span>
                                    <span className="ml-1">
                                        {parcelCountDisplay === null
                                            ? String(t('tms.task.drawer.loading'))
                                            : (parcelCountDisplay ?? 0)}
                                    </span>
                                </Button>
                            ) : (
                                <>
                                    <span className="text-sm text-gray-500 shrink-0">{parcelCountLabel}:</span>
                                    <span className="text-gray-700">
                                        {parcelCountDisplay === null
                                            ? String(t('tms.task.drawer.loading'))
                                            : (parcelCountDisplay ?? 0)}
                                    </span>
                                </>
                            )}
                        </div>
                        {/* 应卸容器数量 todo liuzebiao: 数值待同事提供获取方法 */}
                        <div className="flex items-center gap-3 min-h-[28px] pb-3">
                            <span className="text-sm text-gray-500 shrink-0">{expectedContainerCountLabel}:</span>
                            <span className="text-gray-700">{expectedContainerCount ?? '-'}</span>
                        </div>
                        {/* 包牌列表 */}
                        <div className="flex gap-1 items-start">
                            <div className="mr-3 text-sm text-gray-500">{bagInfoLabel}:</div>
                            <div className="flex-1 max-w-[600px]">
                                <BagInfoTable
                                    dataSource={bagRowsDraft}
                                    taskId={waypoint.taskId}
                                    taskWaypointId={taskWaypointId}
                                    warehouseId={waypoint.waypointId}
                                    routeName={task?.routeName ?? ''}
                                    readOnly={!isEditing || isBagEditDisabled}
                                    isEditing={isEditing}
                                    onChange={isEditing && !isBagEditDisabled ? setBagRowsDraft : undefined}
                                    onEditingStateChange={setIsBagTableEditing}
                                    snapshotRowsByBarCode={bagRowsSnapshotByBarCode}
                                    businessType={task?.businessType}
                                    operationType={TmsOperationTypeEnum.Unloading}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                {/* 卸车BOL照片：仅路线终点站展示 */}
                {waypoint.waypointType === WaypointTypeEnum.Destination &&
                    renderPhotoRow(photoLabel, bolPhotoUrls, bolPhotosDraft, setBolPhotosDraft, bolPhotoUrls)}
                {renderRemarkRow(remarkLabel, waypoint.bolRemark, remarkDraft, setRemarkDraft)}
            </div>
        </div>
    );
};

export default UnloadingInfoSection;
